import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, 
  Box, 
  CircularProgress, 
  Alert, 
  Tabs, 
  Tab, 
  Snackbar,
  Card,
  useTheme,
  alpha,
  Button,
  Chip
} from '@mui/material';
import PrescriptionList from '../../components/PrescriptionList/PrescriptionList';
import { Refresh as RefreshIcon, Sort as SortIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon } from '@mui/icons-material';
import PrescriptionDetail from '../../components/PrescriptionDetail/PrescriptionDetail';
import { 
  fetchAllPrescriptions,
  updatePrescription, 
  createPrescription,
  fetchPrescriptionsByDoctor
} from '../../services/healinApi';
import { useAuth } from '../../auth/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslations } from '../../translations/translations';

function ChronicPrescriptionsHub() {
  const theme = useTheme();
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = useTranslations(language);
  const [availablePrescriptions, setAvailablePrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [claimedPrescriptions, setClaimedPrescriptions] = useState([]);
  const [issuedPrescriptions, setIssuedPrescriptions] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedPrescription, setSelectedPrescription] = useState(null);

  // Sort state
  const [sortBy, setSortBy] = useState('date-desc'); // Default to newest first
  const [sortedAvailablePrescriptions, setSortedAvailablePrescriptions] = useState([]);
  const [sortedClaimedPrescriptions, setSortedClaimedPrescriptions] = useState([]);
  const [sortedIssuedPrescriptions, setSortedIssuedPrescriptions] = useState([]);

  // Sort function
  const sortPrescriptions = (prescriptionsToSort, sortType) => {
    if (!prescriptionsToSort || prescriptionsToSort.length === 0) return [];
    
    const sorted = [...prescriptionsToSort];
    
    switch (sortType) {
      case 'date-asc':
        return sorted.sort((a, b) => {
          const dateA = new Date(a.requestedAt || a.createdAt || 0);
          const dateB = new Date(b.requestedAt || b.createdAt || 0);
          return dateA - dateB;
        });
      case 'date-desc':
        return sorted.sort((a, b) => {
          const dateA = new Date(a.requestedAt || a.createdAt || 0);
          const dateB = new Date(b.requestedAt || b.createdAt || 0);
          return dateB - dateA;
        });
      case 'status':
        return sorted.sort((a, b) => {
          const statusA = (a.status || '').toLowerCase();
          const statusB = (b.status || '').toLowerCase();
          return statusA.localeCompare(statusB);
        });
      case 'patient':
        return sorted.sort((a, b) => {
          const nameA = (a.patientName || '').toLowerCase();
          const nameB = (b.patientName || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      case 'none':
      default:
        return sorted;
    }
  };

  // Handle sort change
  const handleSortChange = (newSortType) => {
    setSortBy(newSortType);
    const sortedAvailable = sortPrescriptions(availablePrescriptions, newSortType);
    const sortedClaimed = sortPrescriptions(claimedPrescriptions, newSortType);
    const sortedIssued = sortPrescriptions(issuedPrescriptions, newSortType);
    setSortedAvailablePrescriptions(sortedAvailable);
    setSortedClaimedPrescriptions(sortedClaimed);
    setSortedIssuedPrescriptions(sortedIssued);
  };

  // Update sorted prescriptions when prescriptions change
  useEffect(() => {
    const sortedAvailable = sortPrescriptions(availablePrescriptions, sortBy);
    const sortedClaimed = sortPrescriptions(claimedPrescriptions, sortBy);
    const sortedIssued = sortPrescriptions(issuedPrescriptions, sortBy);
    setSortedAvailablePrescriptions(sortedAvailable);
    setSortedClaimedPrescriptions(sortedClaimed);
    setSortedIssuedPrescriptions(sortedIssued);
  }, [availablePrescriptions, claimedPrescriptions, issuedPrescriptions, sortBy]);

  // Get sort button text and icon
  const getSortButtonText = () => {
    switch (sortBy) {
      case 'date-asc':
        return t.chronicPrescriptions.dateOldestFirst;
      case 'date-desc':
        return t.chronicPrescriptions.dateNewestFirst;
      case 'status':
        return t.chronicPrescriptions.statusAZ;
      case 'patient':
        return t.chronicPrescriptions.patientNameAZ;
      case 'none':
      default:
        return t.chronicPrescriptions.noSort;
    }
  };

  const getSortIcon = () => {
    switch (sortBy) {
      case 'date-asc':
        return <ArrowUpwardIcon />;
      case 'date-desc':
        return <ArrowDownwardIcon />;
      case 'status':
      case 'patient':
        return <SortIcon />;
      case 'none':
      default:
        return <SortIcon />;
    }
  };

  // Cycle through sort options
  const cycleSortOption = () => {
    const sortOptions = ['date-desc', 'date-asc', 'patient', 'status', 'none'];
    const currentIndex = sortOptions.indexOf(sortBy);
    const nextIndex = (currentIndex + 1) % sortOptions.length;
    handleSortChange(sortOptions[nextIndex]);
  };

  // Fetch available prescriptions using new batch API
  const fetchAvailablePrescriptions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('=== FETCHING AVAILABLE PRESCRIPTIONS ===');
      
      // Use new batch API to fetch pending prescriptions with medication data
      const prescriptions = await fetchAllPrescriptions({
        statuses: ['Pending'], // Only fetch pending prescriptions
        includeMedicationData: true // Include medication details
      });
      
      console.log('Fetched prescriptions:', prescriptions.length);
      
      // Process prescriptions and map medication details properly
      const processedPrescriptions = prescriptions.map(prescription => {
        // Map medicationsDetail to the format expected by PrescriptionCard
        let mappedMedications = [];
        
        if (prescription.medicationsDetail && prescription.medicationsDetail.length > 0) {
          // Use medicationsDetail if available (enriched data)
          mappedMedications = prescription.medicationsDetail
            .filter(med => med !== null) // Filter out null medication details
            .map((med, index) => ({
              id: med.medicationId || `med-${index}`,
              name: med.medicationName || 'Unknown Medication',
              dosage: med.dosage || 'Not specified',
              frequency: med.posology || 'Not specified',
              condition: med.type || null, // chronic, acute, sos
              medicationId: med.medicationId
            }));
        } else if (prescription.medications && prescription.medications.length > 0) {
          // Fallback to basic medication IDs if no detailed data
          mappedMedications = prescription.medications.map((medId, index) => ({
            id: medId || `med-${index}`,
            name: `Medication ${medId.slice(-8)}`,
            dosage: 'Not specified',
            frequency: 'Not specified',
            condition: null,
            medicationId: medId
          }));
        }

        return {
          ...prescription,
          medications: mappedMedications, // Override with properly mapped medications
          patientName: `Patient ${prescription.userId.slice(-8)}`, // Show last 8 chars of userId
          patientInfo: {
            userId: prescription.userId,
            age: 'N/A',
            gender: 'N/A',
            bloodType: 'N/A',
            allergies: [],
            chronicConditions: []
          }
        };
      });
      
              console.log('Processed available prescriptions:', processedPrescriptions.length);
        
        // Debug: Show sample medication mapping
        if (processedPrescriptions.length > 0 && processedPrescriptions[0].medications.length > 0) {
          console.log('Sample medication mapping:', {
            original: prescriptions[0].medicationsDetail?.[0],
            mapped: processedPrescriptions[0].medications[0]
          });
        }
        
        setAvailablePrescriptions(processedPrescriptions || []);
      } catch (err) {
        console.error('Error fetching available prescriptions:', err);
        setError('Failed to load available prescriptions. Please try again later.');
    } finally {
        setLoading(false);
      }
  };

  // Handle prescription claim callback (called after PrescriptionCard successfully claims)
  const handleClaimPrescription = async (requestId) => {
    try {
      // Refresh both lists to reflect the updated status
      await Promise.all([
        fetchAvailablePrescriptions(),
        fetchClaimedPrescriptions()
      ]);
      
      // Show success message
      setSnackbar({
        open: true,
        message: t.chronicPrescriptions.claimSuccessMessage,
        severity: 'success'
      });
      
      return true;
    } catch (err) {
      console.error('Error refreshing after claim:', err);
      
      // Show error message
      setSnackbar({
        open: true,
        message: t.chronicPrescriptions.claimRefreshWarning,
        severity: 'warning'
      });
      
      return false;
    }
  };

  // Issue a prescription
  const handleIssuePrescription = async (prescriptionData) => {
    if (!user) {
      throw new Error('You must be logged in to issue a prescription');
    }
    
    try {
      // Create the prescription with the updated data
      const response = await createPrescription({
        userId: prescriptionData.userId,
        requestId: prescriptionData.requestId,
        medications: prescriptionData.medications,
        status: 'ISSUED',
        source: prescriptionData.source || 'portal',
        comments: prescriptionData.comments || '',
        doctorId: user.username
      });
      
      // Update the original prescription status
      await updatePrescription({
        userId: prescriptionData.userId,
        requestId: prescriptionData.requestId,
        status: 'COMPLETED',
        doctorId: user.username
      });
      
      // Remove from claimed list
      setClaimedPrescriptions(claimedPrescriptions.filter(p => p.requestId !== prescriptionData.requestId));
      
      // Show success message
      setSnackbar({
        open: true,
        message: `Successfully issued ${prescriptionData.medications.length} medication(s)`,
        severity: 'success'
      });
      
      return response;
    } catch (err) {
      console.error('Error issuing prescription:', err);
      
      // Show error message
      setSnackbar({
        open: true,
        message: `Failed to issue prescription: ${err.message}. Please try again.`,
        severity: 'error'
      });
      
      throw err;
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedPrescription(null);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchAvailablePrescriptions();
    fetchClaimedPrescriptions();
    fetchIssuedPrescriptions();
  };

    // Fetch claimed prescriptions using the new doctor-specific API
  const fetchClaimedPrescriptions = useCallback(async () => {
    if (!user || !user.username) {
      setClaimedPrescriptions([]);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('=== FETCHING CLAIMED PRESCRIPTIONS FOR DOCTOR ===');
      console.log('Doctor ID:', user.username);
      
      // Use the new doctor-specific API to fetch claimed prescriptions
      const result = await fetchPrescriptionsByDoctor(
        user.username,
        ['CLAIMED'], // Only fetch claimed prescriptions
        true, // Include medication data
        true  // Include user data
      );
      
      const prescriptions = result.items || [];
      console.log('Fetched claimed prescriptions:', prescriptions.length);
      
      // Process prescriptions with proper medication mapping
      const processedPrescriptions = prescriptions.map(prescription => {
        // Map medicationsDetail to the format expected by PrescriptionCard
        let mappedMedications = [];
        
        if (prescription.medicationsDetail && prescription.medicationsDetail.length > 0) {
          // Use medicationsDetail if available (enriched data)
          mappedMedications = prescription.medicationsDetail
            .filter(med => med !== null) // Filter out null medication details
            .map((med, index) => ({
              id: med.medicationId || `med-${index}`,
              name: med.medicationName || 'Unknown Medication',
              dosage: med.dosage || 'Not specified',
              frequency: med.posology || 'Not specified',
              condition: med.type || null, // chronic, acute, sos
              medicationId: med.medicationId
            }));
        } else if (prescription.medications && prescription.medications.length > 0) {
          // Fallback to basic medication IDs if no detailed data
          mappedMedications = prescription.medications.map((medId, index) => ({
            id: medId || `med-${index}`,
            name: `Medication ${medId.slice(-8)}`,
            dosage: 'Not specified',
            frequency: 'Not specified',
            condition: null,
            medicationId: medId
          }));
        }

        // Extract patient information from userDetail
        const userDetail = prescription.userDetail || {};
        const patientName = userDetail.FirstName && userDetail.LastName 
          ? `${userDetail.FirstName} ${userDetail.LastName}` 
          : userDetail.Name || `Patient ${prescription.userId?.slice(-8) || 'Unknown'}`;

        // Calculate age from DateofBirth if available
        const calculateAge = (dateOfBirth) => {
          if (!dateOfBirth || dateOfBirth === 'NaN') return 'N/A';
          try {
            const birth = new Date(dateOfBirth);
            const today = new Date();
            let age = today.getFullYear() - birth.getFullYear();
            const monthDiff = today.getMonth() - birth.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
              age--;
            }
            return age;
          } catch {
            return 'N/A';
          }
        };

        return {
          ...prescription,
          medications: mappedMedications, // Override with properly mapped medications
          patientName: patientName,
          patientInfo: {
            userId: prescription.userId,
            age: calculateAge(userDetail.DateofBirth),
            gender: userDetail.Sex || 'N/A',
            bloodType: 'N/A', // Not available in userDetail
            allergies: [], // Not available in userDetail
            chronicConditions: [], // Not available in userDetail
            // Additional user details
            email: userDetail.Email || 'N/A',
            phone: userDetail.Phone_Number || 'N/A',
            address: userDetail.Address || 'N/A',
            city: userDetail.City || 'N/A',
            postalCode: userDetail.PostalCode || 'N/A',
            country: userDetail.Country || 'N/A',
            fiscalNumber: userDetail.FiscalNumber || 'N/A',
            nationality: userDetail.Nationality || 'N/A',
            healthDataNumber: userDetail.HealthIDNumber || userDetail.HealthDataNumber || userDetail.SNS_Number || userDetail.HealthID || 'N/A'
          }
        };
      });
      
      console.log('Processed claimed prescriptions:', processedPrescriptions.length);
      
      // Debug: Show sample medication mapping for claimed prescriptions
      if (processedPrescriptions.length > 0 && processedPrescriptions[0].medications.length > 0) {
        console.log('Sample claimed medication mapping:', {
          original: prescriptions[0]?.medicationsDetail?.[0] || prescriptions[0]?.medications?.[0],
          mapped: processedPrescriptions[0].medications[0]
        });
      }
      
      setClaimedPrescriptions(processedPrescriptions);
      } catch (err) {
        console.error('Error fetching claimed prescriptions:', err);
        setError('Failed to load claimed prescriptions. Please try again later.');
    } finally {
        setLoading(false);
      }
  }, [user]);

  // Fetch issued prescriptions using the doctor-specific API
  const fetchIssuedPrescriptions = useCallback(async () => {
    if (!user || !user.username) {
      setIssuedPrescriptions([]);
      return;
    }
    
    try {
      console.log('=== FETCHING ISSUED PRESCRIPTIONS FOR DOCTOR ===');
      console.log('Doctor ID:', user.username);
      
      // Use the doctor-specific API to fetch issued prescriptions
      const result = await fetchPrescriptionsByDoctor(
        user.username,
        ['ISSUED'], // Only fetch issued prescriptions
        true, // Include medication data
        true  // Include user data
      );
      
      const prescriptions = result.items || [];
      console.log('Fetched issued prescriptions:', prescriptions.length);
      
      // Process prescriptions similar to claimed prescriptions
      const processedPrescriptions = prescriptions.map(prescription => {
        // Map medicationsDetail to the format expected by PrescriptionCard
        let mappedMedications = [];
        
        if (prescription.medicationsDetail && prescription.medicationsDetail.length > 0) {
          mappedMedications = prescription.medicationsDetail
            .filter(med => med !== null)
            .map((med, index) => ({
              id: med.medicationId || `med-${index}`,
              name: med.medicationName || 'Unknown Medication',
              dosage: med.dosage || 'Not specified',
              frequency: med.posology || 'Not specified',
              condition: med.type || null,
              medicationId: med.medicationId
            }));
        } else if (prescription.medications && prescription.medications.length > 0) {
          mappedMedications = prescription.medications.map((medId, index) => ({
            id: medId || `med-${index}`,
            name: `Medication ${medId.slice(-8)}`,
            dosage: 'Not specified',
            frequency: 'Not specified',
            condition: null,
            medicationId: medId
          }));
        }

        // Extract patient information from userDetail
        const userDetail = prescription.userDetail || {};
        const patientName = userDetail.FirstName && userDetail.LastName 
          ? `${userDetail.FirstName} ${userDetail.LastName}` 
          : userDetail.Name || `Patient ${prescription.userId?.slice(-8) || 'Unknown'}`;

        // Calculate age from DateofBirth if available
        const calculateAge = (dateOfBirth) => {
          if (!dateOfBirth || dateOfBirth === 'NaN') return 'N/A';
          try {
            const birth = new Date(dateOfBirth);
            const today = new Date();
            let age = today.getFullYear() - birth.getFullYear();
            const monthDiff = today.getMonth() - birth.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
              age--;
            }
            return age;
          } catch {
            return 'N/A';
          }
        };

        return {
          ...prescription,
          medications: mappedMedications,
          patientName: patientName,
          patientInfo: {
            userId: prescription.userId,
            age: calculateAge(userDetail.DateofBirth),
            gender: userDetail.Sex || 'N/A',
            bloodType: 'N/A',
            allergies: [],
            chronicConditions: [],
            email: userDetail.Email || 'N/A',
            phone: userDetail.Phone_Number || 'N/A',
            address: userDetail.Address || 'N/A',
            city: userDetail.City || 'N/A',
            postalCode: userDetail.PostalCode || 'N/A',
            country: userDetail.Country || 'N/A',
            fiscalNumber: userDetail.FiscalNumber || 'N/A',
            nationality: userDetail.Nationality || 'N/A',
            healthDataNumber: userDetail.HealthIDNumber || userDetail.HealthDataNumber || userDetail.SNS_Number || userDetail.HealthID || 'N/A'
          },
          issueDate: prescription.issuedAt || prescription.updatedAt
        };
      });
      
      console.log('Processed issued prescriptions:', processedPrescriptions.length);
      setIssuedPrescriptions(processedPrescriptions);
    } catch (err) {
      console.error('Error fetching issued prescriptions:', err);
      setSnackbar({ 
        open: true, 
        message: `Error fetching issued prescriptions: ${err.message}`, 
        severity: 'error' 
      });
    }
  }, [user]);

  // Initial data fetch
  useEffect(() => {
    fetchAvailablePrescriptions();
    fetchClaimedPrescriptions();
    fetchIssuedPrescriptions();
    
    // Set up polling to refresh data every 5 minutes
    const intervalId = setInterval(() => {
      fetchAvailablePrescriptions();
      fetchClaimedPrescriptions();
      fetchIssuedPrescriptions();
    }, 5 * 60 * 1000);
    
    // Clean up on unmount
    return () => clearInterval(intervalId);
  }, [user, fetchClaimedPrescriptions, fetchIssuedPrescriptions]);

  const handlePrescriptionSelect = (prescription) => {
    setSelectedPrescription(prescription);
  };

  const handleBackToList = () => {
    setSelectedPrescription(null);
  };

  if (loading && !availablePrescriptions.length && !claimedPrescriptions.length && !issuedPrescriptions.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !availablePrescriptions.length && !claimedPrescriptions.length && !issuedPrescriptions.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, mt: 0 }}>
            {t.chronicPrescriptions.title}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {t.chronicPrescriptions.subtitle}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={cycleSortOption}
            startIcon={getSortIcon()}
            sx={{ 
              borderRadius: 8,
              px: 2,
              py: 1,
              minWidth: 200,
              borderColor: alpha(theme.palette.secondary.main, 0.5),
              '&:hover': {
                borderColor: theme.palette.secondary.main,
                bgcolor: alpha(theme.palette.secondary.main, 0.04)
              }
            }}
          >
            {getSortButtonText()}
          </Button>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          sx={{ 
            borderRadius: 8,
            px: 2,
            py: 1,
            borderColor: alpha(theme.palette.primary.main, 0.5),
            '&:hover': {
              borderColor: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.04)
            }
          }}
        >
          {t.chronicPrescriptions.refresh}
        </Button>
        </Box>
      </Box>
      
      {selectedPrescription ? (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                cursor: 'pointer', 
                color: 'primary.main',
                display: 'inline-flex',
                alignItems: 'center',
                '&:hover': { textDecoration: 'underline' }
              }}
              onClick={handleBackToList}
            >
              {t.chronicPrescriptions.backToList}
            </Typography>
          </Box>
          <PrescriptionDetail 
            prescription={selectedPrescription} 
            onIssuePrescription={handleIssuePrescription}
            onClose={handleBackToList}
          />
        </Box>
      ) : (
        <>
          <Card sx={{ mb: 3, overflow: 'visible' }}>
            <Box sx={{ px: 3, pt: 2 }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                aria-label="prescription tabs"
                sx={{
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0'
                  },
                  '& .MuiTab-root': {
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    minHeight: 48,
                    py: 1.5,
                    '&.Mui-selected': {
                      color: theme.palette.primary.main
                    }
                  }
                }}
              >
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {t.chronicPrescriptions.availableRequests.replace('{count}', availablePrescriptions.length)}
                      {sortBy !== 'none' && (
                        <Chip 
                          label={getSortButtonText()} 
                          size="small" 
                          color="primary" 
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  } 
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {t.chronicPrescriptions.myClaimedRequests.replace('{count}', claimedPrescriptions.length)}
                      {sortBy !== 'none' && (
                        <Chip 
                          label={getSortButtonText()} 
                          size="small" 
                          color="primary" 
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  } 
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {t.chronicPrescriptions.myIssuedRequests.replace('{count}', issuedPrescriptions.length)}
                      {sortBy !== 'none' && (
                        <Chip 
                          label={getSortButtonText()} 
                          size="small" 
                          color="primary" 
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  } 
                />
              </Tabs>
            </Box>
            
            <Box sx={{ p: 3 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 6, py: 4 }}>
                  <CircularProgress size={40} thickness={4} />
                </Box>
              ) : (
                <>
                  {tabValue === 0 && (
                    <>
                      <PrescriptionList 
                        prescriptions={sortedAvailablePrescriptions} 
                        onSelectPrescription={handlePrescriptionSelect}
                        onClaimPrescription={handleClaimPrescription}
                        emptyMessage={t.chronicPrescriptions.noAvailableRequests}
                      />
                    </>
                  )}
                  
                  {tabValue === 1 && (
                    <>
                      <PrescriptionList 
                        prescriptions={sortedClaimedPrescriptions} 
                        onSelectPrescription={handlePrescriptionSelect}
                        onIssuePrescription={handleIssuePrescription}
                        isClaimed={true}
                        emptyMessage={t.chronicPrescriptions.noClaimedRequests}
                      />
                    </>
                  )}
                  
                  {tabValue === 2 && (
                    <>
                      <PrescriptionList 
                        prescriptions={sortedIssuedPrescriptions} 
                        onSelectPrescription={handlePrescriptionSelect}
                        isIssued={true}
                        emptyMessage={t.chronicPrescriptions.noIssuedRequests}
                      />
                    </>
                  )}
                </>
              )}
            </Box>
          </Card>
          
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              onClose={handleSnackbarClose} 
              severity={snackbar.severity} 
              sx={{ 
                width: '100%',
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '& .MuiAlert-icon': {
                  alignItems: 'center'
                }
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </>
      )}
    </Box>
  );
}

export default ChronicPrescriptionsHub; 